import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { EtagStore } from './etag-store.service';

/**
 * Watches every response that touches a case (case detail, actions list,
 * action mutations) and records the response's `_etag` against the case id
 * so subsequent mutations can replay it as `If-Match`.
 *
 * Two transport mechanisms are supported:
 *   1. JSON body field `_etag` (Cosmos-style — surfaced verbatim on the case
 *      detail response).
 *   2. HTTP `ETag` response header (returned by the pipeline's mutation
 *      endpoints once they wrap their writes in optimistic-locking).
 *
 * The case id is parsed from the URL (`/cases/<id>`, `/cases/detail?case_id=<id>`,
 * or `/risk/fact-rules/<id>/...`).
 */
@Injectable()
export class EtagCaptureInterceptor implements HttpInterceptor {
  constructor(private readonly etags: EtagStore) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      tap(event => {
        if (!(event instanceof HttpResponse)) return;
        const caseId = this.extractCaseId(req);
        if (!caseId) return;
        const etag = this.extractEtag(event);
        if (etag) this.etags.set(caseId, etag);
      }),
    );
  }

  private extractCaseId(req: HttpRequest<unknown>): string | null {
    // /cases/detail?case_id=<id>
    const fromQuery = req.params.get('case_id');
    if (fromQuery && req.url.includes('/cases/detail')) return fromQuery;

    // /risk/fact-rules/<id>/...
    const riskMatch = /\/risk\/fact-rules\/([^/?]+)/.exec(req.url);
    if (riskMatch) return decodeURIComponent(riskMatch[1]);

    // /cases/<id> or /cases/<id>/...  (excluding /cases or /cases/detail)
    const caseMatch = /\/cases\/([^/?]+)/.exec(req.url);
    if (caseMatch) {
      const candidate = caseMatch[1];
      if (candidate !== 'detail') return decodeURIComponent(candidate);
    }
    return null;
  }

  private extractEtag(res: HttpResponse<unknown>): string | null {
    // Prefer the HTTP header (RFC-correct).
    const headerEtag = res.headers.get('ETag');
    if (headerEtag) return headerEtag;
    // Fall back to a body-level `_etag` (Cosmos passthrough).
    const body = res.body as { _etag?: unknown } | null;
    if (body && typeof body === 'object' && typeof body._etag === 'string') {
      return body._etag;
    }
    return null;
  }
}
